import {WCaseQualifiedFeature, WFeature, WPileEntity, WQualifiedFeature, WQualifier, WRelation} from "./types";

export interface WPolicy extends WPileEntity {
    name: string;
    default?: boolean;
    protocols?: WProtocol[];
}

export enum ProtocolConditionTermType {
    finding = "finding",
    suspected = "suspected",
    answer = "answer",
    accumulator = "accumulator",
    protocolState = "protocolState",
    patientProperty = "patientProperty"
}

// copied from server. this is a superset of question type
export enum ConversationSpecialQuestion {
    Age = 'AGE',
    Allergies = 'ALLERGIES',
    BackgroundSuggestion = 'BACKGROUND_SUGGESTION',
    Gender = 'GENDER',
    Labs = 'LABS',
    Medications = 'MEDICATIONS',
    Pregnancy = 'PREGNANCY',
    VolunteerBackgroud = 'VOLUNTEER_BACKGROUD',
    VolunteerFamilyHistory = 'VOLUNTEER_FAMILY_HISTORY',
    BodyHeight = "BODY_HEIGHT",
    BodyWeight = "BODY_WEIGHT",
    AlcoholConsumption = "ALCOHOL_CONSUMPTION",
    SmokingLevel = "SMOKING_LEVEL",
    COVIDVaccination = "COVID_VACCINATION",
    COVIDExposure = "COVID_EXPOSURE",
    FreeText = 'FREE_TEXT',
    DURATION = 'DURATION',
    ReasonForVisit = 'REASON_FOR_VISIT',
    PregnancyFollowUp = 'PREGNANCY_FOLLOW_UP',
    VitalSignsForm = 'VITALS_FORM',
    FullName = "FULL_NAME",
    DateOfBirth = "DATE_OF_BIRTH",
    ZipCode = "ZIP_CODE",
    PreChatFreeTextComplaints = "PRE_CHAT_FREE_TEXT_COMPLAINTS"
}


export interface ProtocolRuleFindingTerm {
    type: ProtocolConditionTermType.finding,
    finding?: WCaseQualifiedFeature,
    properties?: WQualifier[],
    matchOnly?: boolean,
}
export enum SuspectedDiseaseMetric {
    score = "score",
    urgency = "urgency"
}

export interface ProtocolRuleSuspectedTerm {
    type: ProtocolConditionTermType.suspected,
    suspected?: WQualifiedFeature,
    ranges?: Record<string,{ min?: number,  max?: number}>,
    properties?: WQualifier[]
}

export interface ProtocolRulePatientProperty {
    type: ProtocolConditionTermType.patientProperty,
    propertyKey: string,
    propertyVal: string | number,
}

export enum ConversationMultipleAnswer {
    confirm_some = "confirm_some",
    deny_all = "deny_all",
    skip = "skip"
}
export enum ConversationSingleAnswer {
    yes = "yes",
    no = "no",
    skip = "skip"
}

export enum ProtocolRuleLastAnswerMetric {
    mi = "mi",
    count = "count"
}
export interface ProtocolRuleLastAnswerTerm {
    type: ProtocolConditionTermType.answer,
    overallAnswer?: ConversationMultipleAnswer | null
    findingAnswer?: ConversationSingleAnswer | null
    findingFeature?: WQualifiedFeature | null
    findingProperties?: WQualifier[]
    ranges?: Record<ProtocolRuleLastAnswerMetric,{ min?: number,  max?: number}>
}

export interface ProtocolRuleLastAnswerTerm {
    type: ProtocolConditionTermType.answer,
    overallAnswer?: ConversationMultipleAnswer | null
    findingAnswer?: ConversationSingleAnswer | null
    findingFeature?: WQualifiedFeature | null
    findingProperties?: WQualifier[]
    ranges?: Record<ProtocolRuleLastAnswerMetric,{ min?: number,  max?: number}>
}

export enum ProtocolStateMatch {
    NO_MORE_QUESTIONS= "no_more_questions"
}
export interface ProtocolRuleStateTerm {
    type: ProtocolConditionTermType.protocolState,
    matchState: ProtocolStateMatch
}

export enum AccumlateOperation {
    add = "add",
    multiply = "multiply"
}

export const AccumulateOperationSigns = [
    {operation:AccumlateOperation.add, sign:"+"},
    {operation:AccumlateOperation.multiply, sign:"x"}
]

export interface AccumulateAction {
    /** the variable name to accumulate */
    variable: string;
    op: AccumlateOperation
    value: number;
}

export interface ProtocolRuleAccumulateTerm {
    type: ProtocolConditionTermType.accumulator,
    variable: string;
    range: {min?: number, max?: number}
}

export type ProtocolRuleTerm = ProtocolRuleSuspectedTerm | ProtocolRuleFindingTerm | ProtocolRuleLastAnswerTerm | ProtocolRuleAccumulateTerm | ProtocolRuleStateTerm | ProtocolRulePatientProperty;

export type ProtocolRuleCondition = (ProtocolRuleConditionV1|ProtocolRuleConditionV2);

interface TermsOf<T> {
    terms: T[];
}

interface ProtocolRuleConditionV2 extends TermsOf<ProtocolRuleTerm>{
    findings?: never;
}


interface ProtocolRuleConditionV1 {
    findings: WCaseQualifiedFeature[];
    terms?: never
}

/**
 * @deprecated
 */
export enum ConversationTouchpoint {
    IMMEDIATE = 0,
    AFTER_REFINEMENT = 1,
    AFTER_ENTROPY = 2,
    POST_CONVERSATION = 3,
    AFTER_INITIAL_QUESTIONS = 4
}

export enum TouchpointTiming {
    BEFORE = "before", AFTER = "after"
}


export enum ConversationFlowPhase {
    Intro = "intro",
    Personal = "personal",
    Background = "background",
    Complaint = "complaint",
    Suggestions = "suggestions",
    Closing = "closing",
    MandatoryRefinedFinding = "complaint + mandatory refinements",
    RefinedFinding = "complaint + all refinements",
}


export interface PhaseTouchpoint {
    phase: ConversationFlowPhase; // a flow-phase
    timing: TouchpointTiming
}

enum ProtocolActionsType {
    AvoidRefinements
}

type protocolAction = {
    type: ProtocolActionsType
}

export interface WProtocol extends WPileEntity {
    id: string;
    name: string;
    /** @deprecated */
    policyid?: string,
    /** @deprecated */
    priority?: ConversationTouchpoint,
    order?: number,
    /**
     * @deprecated - now support multiple conditions
     */
    condition?: ProtocolRuleCondition; // legacy
    touchpoint?: PhaseTouchpoint; // if null, then timing is immediate (i.e. always)
    conditions?: ProtocolRuleCondition[]; // in use
    rules?: WProtocolRule[];
    tags?: string[];
    enabled?: boolean; // protocol is live in production when ENABLED && !PRE_RELEASE
    preRelease?: boolean; // pre-release === test stage == protocol is only live in dev environments
    isCriteriaProtocol?: boolean;
    actions?: protocolAction[];
}
export namespace WProtocol {
    export const isCriteriaProtocol = (p:WProtocol): p is WCriteriaProtocol => p?.isCriteriaProtocol
}

export enum CriteriaPurpose {
    Validation = "Validation",
    Refinement = "Refinement",
    Calculation = "Calculation",
    MatchOnly = "MatchOnly",
    /** @deprecated*/ Suggestion = "Suggestion" // not in use
}

export interface WCriteriaProtocol extends WProtocol {
    isCriteriaProtocol: true,
    criteriaPurpose?: CriteriaPurpose,
    citationid: string,
    policyid?: never,
    priority?: never,
    condition?: never,
    touchpoint?: never,

    definesFeature: WFeature,
    definesRelation?: WRelation,
    useAsPatientVerification?:boolean,
    conditions?: TermsOf<ProtocolRuleFindingTerm>[];
    rules?: WCriteriaRule[];
}


/**
 * outcome for general purpose protocols
 */
export interface ProtocolOutcome {
    recommendations?: WQualifiedFeature[],
    specialQuestions?: ConversationSpecialQuestion[],
    findingQuestions?: WCaseQualifiedFeature[],
    accumulators?: AccumulateAction[],
    endProtocol?: boolean,
}


/**
 * outcomes for criteria protocols
 */
export interface CriteriaProtocolOutcome extends ProtocolOutcome {
    infer?: WCaseQualifiedFeature[];
    accumulators?: AccumulateAction[];
    findingQuestions?: WCaseQualifiedFeature[],

    recommendations?: never,
    specialQuestions?: never,
    endProtocol?: never,
}

export interface WProtocolRule {
    condition: ProtocolRuleCondition
    whenTrue? :ProtocolOutcome,
    /**
     * @deprecated no longer supported
     */
    whenFalse?: ProtocolOutcome
}

/**
 * rules for Criteria
 */
export interface WCriteriaRule extends WProtocolRule {
    condition: TermsOf<ProtocolRuleAccumulateTerm | ProtocolRuleFindingTerm>,
    whenTrue?: CriteriaProtocolOutcome,
    whenFalse?: never
}

export interface SavePolicyRequest {
    policy: WPolicy;
    commitComment?: string;
    parentCommitid?: string;
}

export interface SavePolicyResponse {
    status: string;
    policy: WPolicy;
}

export interface SaveProtocolRequest {
    protocol: WProtocol;
    policy: WPolicy;
    commitComment?: string;
}

export interface SaveProtocolResponse {
    status: string;
    protocol: WProtocol;
}

export function convertV1ProtocolCondition(c: ProtocolRuleCondition) {
    if (c?.findings) {
        const terms = c.findings.map(
            (finding) =>
                ({
                    finding,
                    type: ProtocolConditionTermType.finding,
                } as ProtocolRuleFindingTerm),
        );
        delete c.findings;
        c.terms = terms;
    }
}
export function convertV1ProtocolTouchpoint(p: WProtocol) {

    const mapTouchpointToPhase = (t:ConversationTouchpoint): PhaseTouchpoint =>{
        switch (t) {
            case ConversationTouchpoint.AFTER_ENTROPY:
                return {phase: ConversationFlowPhase.Suggestions, timing: TouchpointTiming.AFTER};
            case ConversationTouchpoint.AFTER_INITIAL_QUESTIONS:
                return { phase: ConversationFlowPhase.Personal, timing: TouchpointTiming.AFTER};
            case ConversationTouchpoint.AFTER_REFINEMENT:
                return { phase: ConversationFlowPhase.Suggestions, timing: TouchpointTiming.BEFORE};
            case ConversationTouchpoint.POST_CONVERSATION:
                return { phase: ConversationFlowPhase.Closing, timing: TouchpointTiming.BEFORE};
            case ConversationTouchpoint.IMMEDIATE:
                return null;
        }
    }
    if (!p?.touchpoint && p?.priority != ConversationTouchpoint.IMMEDIATE) {
        p.touchpoint = mapTouchpointToPhase(p?.priority);
    }
}

export function convertLegacyWProtocol(p: WProtocol) {
    convertV1ProtocolCondition(p?.condition);
    convertV1ProtocolTouchpoint(p);
    if (p?.condition && !p?.conditions) {
        p.conditions = [p.condition];
    }
    p?.rules?.forEach(r=>{
        convertV1ProtocolCondition(r?.condition);
    });
    if (p && WProtocol.isCriteriaProtocol(p) && !p.criteriaPurpose) {
        p.criteriaPurpose = CriteriaPurpose.Validation;
    }
}
